<script setup lang="ts">
import { useForm, useField } from 'vee-validate'
import { reactive, ref, onMounted, computed } from 'vue'
import * as yup from 'yup'

import FooterBottom from '@/components/Footer/FooterBottom.vue'
import SocialsWrapper from '@/components/Footer/SocialsWrapper.vue'
import HantecLogo from '@/components/Icons/HantecLogo.vue'
import SuccessPopup from '@/components/SuccessPopup.vue'
import getPreferenceCenter from '@/services/Forms/getPreferenceCenter'
import updatePreferenceCenter from '@/services/Forms/updatePreferenceCenter'
import { useFooterData } from '@/services/seoGlobalService'
import { __ } from '@/utils/utils'

interface Channels {
  sms: boolean
  whatsapp: boolean
}

interface Markets {
  forex: boolean
  stocks: boolean
  metals: boolean
  cryptocurrency: boolean
  gold: boolean
  indices: boolean
  commodities: boolean
}

interface MarketItem {
  key: keyof Markets
  label: string
}

interface MoEngageCredentials {
  user_id?: string
  cid?: string
}

interface MoEngageUserDetails {
  first_name?: string
  last_name?: string
  name?: string
  country?: string
  email?: string
  customer_id?: string
  id?: string
  client_compliance_status?: string
  brazil_regulatory_insightpro_TNCs?: boolean
  client_country?: string
}

interface MoEngagePreferenceCategories {
  New_Product_Launches?: boolean
  Product_Recommendations?: boolean
  Promotional_emails?: boolean
  Operational_emails?: boolean
  Market_Analysis_Dynamic_Emails?: boolean
  Marketing_Consent?: boolean
  [key: string]: boolean | undefined
}

interface MoEngageGetResponse {
  user_details: MoEngageUserDetails
  user_preference_categories: MoEngagePreferenceCategories
}

const upperFooterData = useFooterData()

const channels = reactive<Channels>({
  sms: false,
  whatsapp: false
})

const markets = reactive<Markets>({
  forex: false,
  stocks: false,
  metals: false,
  cryptocurrency: false,
  gold: false,
  indices: false,
  commodities: false
})

const isLoading = ref<boolean>(false)
const userEmail = ref<string>('')
const credentials = ref<MoEngageCredentials>({})
const showSuccessMessage = ref<boolean>(false)

const showChannelsSection = ref<boolean>(false)
const showInsightProSections = ref<boolean>(false)
const clientComplianceStatus = ref<boolean>(false)

const emailRegex =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-z0-9!#$%&*+=?_.-]+(?:\.[a-z0-9!#$%&*+=?_.-]+)*@(?=.{1,189}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i

const marketsList: MarketItem[] = [
  { key: 'forex', label: 'Forex' },
  { key: 'stocks', label: 'Stocks' },
  { key: 'metals', label: 'Metals' },
  { key: 'cryptocurrency', label: 'Cryptocurrency' },
  { key: 'gold', label: 'Gold' },
  { key: 'indices', label: 'Indices' },
  { key: 'commodities', label: 'Commodities' }
]

// Validation schema
const validationSchema = computed(() => {
  const baseSchema: any = {
    marketingConsent: yup.boolean().nullable().required(__('This field is required'))
  }

  if (showInsightProSections.value) {
    baseSchema.insightProConsent = yup.boolean().nullable().required(__('This field is required'))

    if (insightProConsent.value) {
      baseSchema.markets = yup
        .object()
        .test('at-least-one-market', __('Please select at least one market'), (value: any) => {
          return Object.values(value || {}).some((v) => v === true)
        })
    }

    baseSchema.pushNotifications = yup.boolean().nullable().required(__('This field is required'))
  }

  return yup.object(baseSchema)
})

const { handleSubmit, errors, setFieldValue } = useForm({
  validationSchema
})

const selectedMarketsCount = computed(() => {
  return Object.values(markets).filter((value) => value === true).length
})

const isMarketDisabled = (marketKey: keyof Markets) => {
  return !insightProConsent.value || (selectedMarketsCount.value >= 2 && !markets[marketKey])
}

const { value: marketingConsent } = useField<boolean | null>('marketingConsent', undefined, {
  initialValue: null
})
const { value: insightProConsent } = useField<boolean | null>('insightProConsent', undefined, {
  initialValue: null
})
const { value: pushNotifications } = useField<boolean | null>('pushNotifications', undefined, {
  initialValue: null
})

const sanitizeEmail = (email: string): string => {
  const normalizedEmail = (email || '')
    .trim()
    .replace(/^mailto:/i, '')
    .replace(/[<>]/g, '')
    .replace(/^['"]+|['"]+$/g, '')
    .replace(/\s+/g, '')

  const extractedEmail = normalizedEmail.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/)
  return extractedEmail ? extractedEmail[0] : normalizedEmail
}

const isValidEmail = (email: string): boolean => {
  return emailRegex.test(email)
}

const getValidSanitizedEmail = (): string | null => {
  const sanitizedEmail = sanitizeEmail(userEmail.value)

  if (!isValidEmail(sanitizedEmail)) {
    console.error('Invalid email format')
    return null
  }

  userEmail.value = sanitizedEmail
  return sanitizedEmail
}

const getUrlParams = (): void => {
  const searchWithEncodedPlus = window.location.search.replace(/\+/g, '%2B')
  const urlParams = new URLSearchParams(searchWithEncodedPlus)

  const userIds = urlParams.getAll('user_id')
  const cid = urlParams.get('cid')

  if (userIds.length >= 1) {
    userEmail.value = sanitizeEmail(userIds[0])
  }

  if (userIds.length >= 2) {
    credentials.value.user_id = userIds[1]
  }

  if (cid) {
    credentials.value.cid = cid
  }
}

const loadUserPreferences = async (): Promise<void> => {
  const validEmail = getValidSanitizedEmail()

  if (!validEmail) {
    console.error('Valid email is required')
    return
  }

  isLoading.value = true

  try {
    const envVar = import.meta.env.VITE_MOE_ENV
    const requestBody: any = {
      action: 'get',
      email: validEmail,
      parameters: [
        'country',
        'client_country',
        'web_campaign',
        'market_forex',
        'market_stocks',
        'market_metals',
        'market_cryptocurrency',
        'market_gold',
        'market_indices',
        'market_commodities',
        'moe_sms_subscription',
        'moe_wa_subscription',
        'push_notifications_mt4_mt5',
        'client_compliance_status',
        'client_crm_compliance_status',
        'brazil_regulatory_insightpro_TNCs'
      ]
    }

    if (envVar) {
      requestBody.env = envVar
    }

    if (credentials.value.user_id && credentials.value.cid) {
      requestBody.credentials = credentials.value
    }

    const response = await getPreferenceCenter.get(requestBody)

    if (response.status !== 200) {
      throw new Error('Failed to load preferences')
    }

    const data = response.data as MoEngageGetResponse
    const prefs = data.user_preference_categories
    const details = data.user_details as any

    if (prefs) {
      setFieldValue('marketingConsent', prefs.Marketing_Consent ?? null)
      showChannelsSection.value = prefs.Marketing_Consent === true

      const marketAnalysisEnabled = !!prefs.Market_Analysis_Dynamic_Emails
      showInsightProSections.value = marketAnalysisEnabled
      setFieldValue('insightProConsent', marketAnalysisEnabled)

      if (details) {
        const marketMap: Array<[keyof Markets, string, boolean?]> = [
          ['forex', 'market_forex', true],
          ['stocks', 'market_stocks'],
          ['metals', 'market_metals'],
          ['cryptocurrency', 'market_cryptocurrency'],
          ['gold', 'market_gold', true],
          ['indices', 'market_indices'],
          ['commodities', 'market_commodities']
        ]

        for (const [key, source, defaultValue] of marketMap) {
          if (details[source] !== undefined) {
            markets[key] = Boolean(details[source])
          } else if (defaultValue !== undefined) {
            markets[key] = defaultValue
          } else {
            markets[key] = false
          }
        }

        channels.sms = (details.sms_subscription_status ?? '') === 'OPTIN'
        channels.whatsapp = (details.whatsapp_subscription_status ?? '') === 'OPTIN'

        const isComplianceApproved =
          details?.client_compliance_status === 'id_compliance_approved' ||
          details?.client_compliance_status === 'compliance_approved'

        if (
          details?.client_country?.toLowerCase === 'brazil' ||
          details?.country?.toLowerCase === 'brazil'
        ) {
          clientComplianceStatus.value =
            isComplianceApproved && details?.brazil_regulatory_insightpro_TNCs
        } else {
          clientComplianceStatus.value = isComplianceApproved
        }

        setFieldValue('channels', { ...channels })
        setFieldValue('markets', { ...markets })
        setFieldValue('pushNotifications', Boolean(details.push_notifications_mt4_mt5))
      }
    }

    if (details?.id) {
      credentials.value.user_id = details.id as string
    }
  } catch (error) {
    console.error('Error loading preferences:', error)
  } finally {
    isLoading.value = false
  }
}

const handleMarketingConsentChange = (): void => {
  showChannelsSection.value = marketingConsent.value === true
  setFieldValue('channels', channels)

  if (marketingConsent.value === false) {
    channels.sms = false
    channels.whatsapp = false
  }
}

const handleInsightProConsentChange = (): void => {
  if (insightProConsent.value === false) {
    Object.keys(markets).forEach((key) => {
      markets[key as keyof Markets] = false
    })
    setFieldValue('pushNotifications', false)
  }
  setFieldValue('markets', markets)
}

const handleChannelChange = (): void => {
  setFieldValue('channels', channels)
}

const handleMarketChange = (): void => {
  setFieldValue('markets', markets)
}

const onSubmit = handleSubmit(async () => {
  const validEmail = getValidSanitizedEmail()

  if (!validEmail) {
    return
  }

  isLoading.value = true

  try {
    const envVar = import.meta.env.VITE_MOE_ENV
    const requestBody: any = {
      action: 'post',
      email: validEmail,
      parameters: {
        market_forex: markets.forex,
        market_stocks: markets.stocks,
        market_metals: markets.metals,
        market_cryptocurrency: markets.cryptocurrency,
        market_gold: markets.gold,
        market_indices: markets.indices,
        market_commodities: markets.commodities,
        push_notifications_mt4_mt5: pushNotifications.value,
        moe_sms_subscription: channels.sms ? 'OPTIN' : 'OPTOUT',
        moe_wa_subscription: channels.whatsapp ? 'OPTIN' : 'OPTOUT'
      },
      update_categories: {
        Marketing_Consent: marketingConsent.value ?? false,
        Market_Analysis_Dynamic_Emails: insightProConsent.value ?? false
      },
      unsubscribe_all: false
    }

    if (envVar) {
      requestBody.env = envVar
    }

    const response = await updatePreferenceCenter.post(requestBody)

    if (response.status !== 200) {
      throw new Error('Failed to update preferences')
    }

    showSuccessMessage.value = true
  } catch (error) {
    console.error('Error updating preferences:', error)
  } finally {
    isLoading.value = false
  }
})

onMounted(() => {
  getUrlParams()

  if (userEmail.value) {
    loadUserPreferences()
  }
})

const closePopup = () => {
  showSuccessMessage.value = false
}
</script>

<template>
  <div class="min-h-screen bg-black text-white flex items-center justify-center preference-center">
    <div class="w-[95%] px-4 md:w-[90%]">
      <!-- Header -->
      <div class="w-full mb-24 py-5">
        <HantecLogo class="block pl-0.5 pr-3 w-36 h-8" />
      </div>

      <div class="text-center mb-8">
        <h1
          class="text-zinc-50 text-center text-3xl md:text-5xl font-semibold leading-tight md:leading-tight tracking-tight mb-6"
        >
          {{ __('Communication Preference Center') }}
        </h1>
        <p
          class="text-zinc-400 text-center text-base md:text-xl font-normal leading-6 md:leading-8"
        >
          {{ __('Control how you receive updates insights and signals from Hantec Markets.') }}
        </p>
      </div>

      <!-- Form Container -->
      <form
        @submit="onSubmit"
        class="rounded-2xl bg-neutral-900/65 p-6 md:px-8 md:py-10 space-y-8 max-w-2xl mx-auto mt-10 md:mt-16 mb-12 md:mb-24"
      >
        <!-- Marketing Content Section -->
        <div class="space-y-4">
          <h2 class="text-lg font-semibold">{{ __('Marketing Content') }}</h2>

          <div class="space-y-3">
            <div>
              <p class="text-zinc-300 text-sm font-medium leading-5 mb-3">
                1)
                {{
                  __(
                    'Stay ahead with updates, market insights, special offers, and educational resources from Hantec Markets.'
                  )
                }}
                <span class="text-red-400">*</span>
              </p>
              <div class="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input
                      type="radio"
                      v-model="marketingConsent"
                      :value="true"
                      @change="handleMarketingConsentChange"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                        marketingConsent === true ? 'bg-red-600 border-red-600' : 'border-zinc-500'
                      ]"
                    >
                      <div
                        v-if="marketingConsent === true"
                        class="w-2.5 h-2.5 bg-white rounded-full"
                      ></div>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('Yes, I agree') }}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input
                      type="radio"
                      v-model="marketingConsent"
                      :value="false"
                      @change="handleMarketingConsentChange"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                        marketingConsent === false ? 'bg-red-600 border-red-600' : 'border-zinc-500'
                      ]"
                    >
                      <div
                        v-if="marketingConsent === false"
                        class="w-2.5 h-2.5 bg-white rounded-full"
                      ></div>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('No, I do not agree') }}</span>
                </label>
              </div>
              <p v-if="errors.marketingConsent" class="text-red-300 text-sm leading-5 mt-2">
                {{ errors.marketingConsent }}
              </p>
            </div>

            <div>
              <p class="text-zinc-300 text-sm font-medium leading-5 mb-3">
                2) {{ __('Manage your preferred additional channels') }}
              </p>
              <div class="flex flex-wrap gap-4">
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input
                      type="checkbox"
                      v-model="channels.sms"
                      @change="handleChannelChange"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded flex items-center justify-center transition-all',
                        channels.sms ? 'bg-red-600' : 'bg-transparent border-2 border-zinc-500'
                      ]"
                    >
                      <svg
                        v-if="channels.sms"
                        class="w-3.5 h-3.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('SMS') }}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input
                      type="checkbox"
                      v-model="channels.whatsapp"
                      @change="handleChannelChange"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded flex items-center justify-center transition-all',
                        channels.whatsapp ? 'bg-red-600' : 'bg-transparent border-2 border-zinc-500'
                      ]"
                    >
                      <svg
                        v-if="channels.whatsapp"
                        class="w-3.5 h-3.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('Whatsapp') }}</span>
                </label>
              </div>
              <p v-if="errors.channels" class="text-red-300 text-sm leading-5 mt-2">
                {{ errors.channels }}
              </p>
            </div>
          </div>
        </div>

        <!-- InsightPro Section -->
        <div v-if="clientComplianceStatus" class="space-y-4">
          <h2 class="text-lg font-semibold">{{ __('InsightPro Dynamic Trading Signals') }}</h2>

          <div class="space-y-3">
            <div>
              <p class="text-zinc-300 text-sm font-medium leading-5 mb-3">
                3)
                {{
                  __(
                    'Receive real-time, AI-powered trade ideas directly from InsightPro to help you trade smarter.'
                  )
                }}
                <span class="text-red-400">*</span>
              </p>
              <div class="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input
                      type="radio"
                      v-model="insightProConsent"
                      :value="true"
                      @change="handleInsightProConsentChange"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                        insightProConsent === true ? 'bg-red-600 border-red-600' : 'border-zinc-500'
                      ]"
                    >
                      <div
                        v-if="insightProConsent === true"
                        class="w-2.5 h-2.5 bg-white rounded-full"
                      ></div>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('Yes, I agree') }}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input
                      type="radio"
                      v-model="insightProConsent"
                      :value="false"
                      @change="handleInsightProConsentChange"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                        insightProConsent === false
                          ? 'bg-red-600 border-red-600'
                          : 'border-zinc-500'
                      ]"
                    >
                      <div
                        v-if="insightProConsent === false"
                        class="w-2.5 h-2.5 bg-white rounded-full"
                      ></div>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('No, I do not agree') }}</span>
                </label>
              </div>
              <p v-if="errors.insightProConsent" class="text-red-300 text-sm leading-5 mt-2">
                {{ errors.insightProConsent }}
              </p>
            </div>

            <div>
              <p class="text-zinc-300 text-sm font-medium leading-5 mb-3">
                4)
                {{
                  __('If yes, choose the markets you’d like signals for (select all that apply):')
                }}
              </p>
              <div class="grid grid-cols-2 gap-3">
                <label
                  v-for="market in marketsList"
                  :key="market.key"
                  class="flex items-center gap-2 cursor-pointer text-zinc-400"
                  :class="{ 'opacity-50 cursor-not-allowed': isMarketDisabled(market.key) }"
                >
                  <div class="relative">
                    <input
                      type="checkbox"
                      v-model="markets[market.key]"
                      @change="handleMarketChange"
                      :disabled="isMarketDisabled(market.key)"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded flex items-center justify-center transition-all',
                        markets[market.key]
                          ? 'bg-red-600'
                          : 'bg-transparent border-2 border-zinc-500',
                        isMarketDisabled(market.key) && 'opacity-50'
                      ]"
                    >
                      <svg
                        v-if="markets[market.key]"
                        class="w-3.5 h-3.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <span class="text-sm">{{ __(market.label) }}</span>
                </label>
              </div>
              <p v-if="errors.markets" class="text-red-300 text-sm leading-5 mt-2">
                {{ errors.markets }}
              </p>
            </div>

            <div>
              <p class="text-zinc-300 text-sm font-medium leading-5 mb-3">
                5) {{ __('Would you like to receive InsightPro push notifications on MT4/MT5?') }}
                <span class="text-red-400">*</span>
              </p>
              <div class="flex gap-6">
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input type="radio" v-model="pushNotifications" :value="true" class="sr-only" />
                    <div
                      :class="[
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                        pushNotifications === true ? 'bg-red-600 border-red-600' : 'border-zinc-500'
                      ]"
                    >
                      <div
                        v-if="pushNotifications === true"
                        class="w-2.5 h-2.5 bg-white rounded-full"
                      ></div>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('Yes') }}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer text-zinc-400">
                  <div class="relative">
                    <input
                      type="radio"
                      v-model="pushNotifications"
                      :value="false"
                      class="sr-only"
                    />
                    <div
                      :class="[
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                        pushNotifications === false
                          ? 'bg-red-600 border-red-600'
                          : 'border-zinc-500'
                      ]"
                    >
                      <div
                        v-if="pushNotifications === false"
                        class="w-2.5 h-2.5 bg-white rounded-full"
                      ></div>
                    </div>
                  </div>
                  <span class="text-sm">{{ __('No') }}</span>
                </label>
              </div>
              <p v-if="errors.pushNotifications" class="text-red-300 text-sm leading-5 mt-2">
                {{ errors.pushNotifications }}
              </p>
            </div>
          </div>
        </div>

        <!-- Success Message -->
        <SuccessPopup
          :is-open="showSuccessMessage"
          :message="__('Your preference is submitted successfully')"
          @close="closePopup"
        />

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="isLoading"
          class="w-full text-white font-semibold py-3 px-6 rounded-3xl transition-colors duration-200 disabled:cursor-not-allowed disabled:bg-zinc-600 bg-red-600 hover:bg-red-700"
        >
          {{ __('Update my preferences') }}
        </button>
      </form>
    </div>
  </div>

  <footer class="mx-auto bg-black text-white py-5 md:py-12 min-h-[200px] md:min-h-[300px]">
    <div class="flex flex-col w-[90%] mx-auto gap-10 md:gap-10 lg:gap-20">
      <div
        class="flex flex-col items-center justify-between gap-10 md:gap-0 md:flex-row lg:px-5 pb-16 px-2.5 border-b border-zinc-700"
      >
        <SocialsWrapper
          :twitter_link="upperFooterData?.twitter_link"
          :linkedin_link="upperFooterData?.linkedin_link"
          :facebook_link="upperFooterData?.facebook_link"
          :instagram_link="upperFooterData?.instagram_link"
          class="order-2 md:order-none px-2.5 lg:px-0"
        />
        <img
          :alt="upperFooterData?.hantec_logo_alt"
          class="h-8 md:h-auto lg:block"
          loading="lazy"
          :src="upperFooterData?.hantec_logo"
        />
      </div>
      <FooterBottom />
    </div>
  </footer>
</template>

<style scoped>
.preference-center {
  background-image: url('/assets/red-background-compressed.webp');
  background-position: center 300px;
  background-repeat: no-repeat;
  background-size: auto;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
